/**
 * Types for the Accounts Payable module.
 *
 * Field names mirror the Rails AP models (Ap::Invoice, Ap::InvoiceItem,
 * Ap::Receipt, Ap::ReceiptItem, Ap::AgentRun, Ap::AgentAction,
 * Ap::PricebookReviewItem, Ap::Email, Ap::Attachment, Ap::Vendor) so the
 * serialized API responses map directly onto these interfaces.
 */

/**
 * The kinds of purchase document AP processes. Invoices (billed, paid later)
 * and receipts (paid at the counter) are separate records with separate
 * endpoints; they share only the fields in `ApPurchaseDocument`.
 */
export type ApDocumentType = "invoice" | "receipt";

export type ApInvoiceStatus =
  | "pending_parse"
  | "parsing"
  | "parsed"
  | "matching"
  | "matched"
  | "match_failed"
  | "parse_failed";

export type ApReviewStatus =
  | "pending_review"
  | "approved"
  | "applying"
  | "applied"
  | "rejected"
  | "apply_failed";

/** Rollback lifecycle of an applied action */
export type ApRollbackStatus =
  | "not_rolled_back"
  | "rolling_back"
  | "rolled_back"
  | "rollback_failed"
  | "rollback_blocked";

export type ApItemType = "material" | "equipment";

/**
 * How the invoice's ServiceTitan job was found (the api repo's
 * `Ap::Invoice::MATCH_STRATEGIES`). `non_job` is the odd one out: it records
 * that there is no job behind the purchase rather than how one was found.
 * `purchase_order` can also leave `st_job_external_id` null — ServiceTitan
 * allows job-less POs — so "matched with no job", not this value, is what tells
 * you an invoice is a non-job expense.
 */
export type ApJobMatchStrategy =
  | "purchase_order"
  | "job_number"
  | "customer_name"
  | "agent"
  | "manual"
  | "non_job";

export const AP_ACTION_TYPE = {
  createPo: "create_po",
  createPricebookItem: "create_pricebook_item",
  createReturn: "create_return",
  flagForReview: "flag_for_review",
  linkVendor: "link_vendor",
  markNonJob: "mark_non_job",
  matchJob: "match_job",
  mergeVendor: "merge_vendor",
  noAction: "no_action",
  updatePo: "update_po",
  updatePricebookCost: "update_pricebook_cost",
} as const;

export type ApActionType = (typeof AP_ACTION_TYPE)[keyof typeof AP_ACTION_TYPE];

export type ApAgentRunStatus = "pending" | "running" | "completed" | "failed";

/**
 * `qualified_selected` means the run's remaining proposals were auto-approved
 * and applied without human review.
 */
export type ApAutomationQualification =
  | "disqualified"
  | "qualified_skipped"
  | "qualified_selected";

export type ApEmailStatus =
  | "received"
  | "processing"
  | "processed"
  | "partial_failure"
  | "failed";

export type ApAttachmentStatus =
  | "pending"
  | "classifying"
  | "is_invoice"
  | "is_receipt"
  | "not_invoice"
  | "download_failed"
  | "classification_failed"
  | "unsupported_type";

export interface ApInvoiceItem {
  id: string;
  description: string;
  sku: string | null;
  quantity: number;
  unit_cost: number;
  item_type: ApItemType;
  /** The matched ServiceTitan pricebook item's id (with its type, the ST key). */
  matched_pricebook_item_id?: number | null;
  matched_pricebook_item_type?: ApItemType | null;
  matched_pricebook_item_name: string | null;
  pricebook_cost: number | null;
  /** pricebook_cost - unit_cost. Positive = pricebook is higher than invoice. */
  cost_difference: number | null;
  /**
   * "manual" when a user chose the line's pricebook item (or chose none);
   * null for an automatic match. A manual choice survives a re-match.
   */
  pricebook_match_strategy?: "manual" | null;
  pricebook_matched_at?: string | null;
  /**
   * How many of the document's lines share this one's SKU, itself included.
   * A hand pick covers them all. Sent with a document's lines; absent where a
   * line is serialized on its own.
   */
  same_sku_line_count?: number;
}

/**
 * One item from the business's synced ServiceTitan pricebook, as the manual
 * line → pricebook item search returns it (`GET .../ap/pricebook_items?q=`).
 * `id` is ours and is what the match request sends; `external_id` is ST's.
 */
export interface ApPricebookItemOption {
  id: string;
  external_id: number;
  item_type: ApItemType;
  code: string | null;
  name: string | null;
  description: string | null;
  /** Item-level cost, as a decimal string; null when ST records none. */
  cost: string | null;
  /** Invisible in the ST pricebook UI (an unadopted import). */
  uncategorized: boolean;
}

/**
 * A ServiceTitan purchase order a person can attribute a document to, as the
 * manual PO picker lists them (`GET .../:kind/:id/purchase_orders?q=`).
 */
export interface ApPurchaseOrderOption {
  st_purchase_order_id: number;
  number: string | null;
  status: string | null;
  st_job_external_id: number | null;
  /** Known only for jobs Volca has seen; null otherwise. */
  job_number: string | null;
  st_vendor_id: number | null;
  /** From a vendor Volca has linked; null for one it hasn't. */
  vendor_name: string | null;
  /** Whether the PO is from the document's own (linked) vendor. */
  same_vendor: boolean;
  /**
   * Whether picking it moves the document off its job (the PO sits on
   * another job, or on none). Optional until the API that sends it is deployed.
   */
  moves_job?: boolean;
  total: number | null;
  date: string | null;
  vendor_document_number: string | null;
  memo: string | null;
  /** The PO the document already carries. */
  current: boolean;
  /** Voided in ServiceTitan; the server refuses it, so it is not pickable. */
  canceled: boolean;
}

export interface ApSystemMessage {
  id: string;
  content: string;
  is_markdown?: boolean;
  created_at: string;
}

/**
 * The API's review states (`Ap::DocumentReview::REVIEW_STATES`). Only
 * needs_review and reviewed are badged and filterable in the web.
 */
export type ApReviewState = "awaiting_agent" | "needs_review" | "reviewed";

/**
 * What every purchase document serializes, whatever its kind (the api repo's
 * `Ap::PurchaseDocumentSerializer`). Components that only need these fields
 * take this type, so they work for invoices and receipts alike.
 */
export interface ApPurchaseDocument {
  id: string;
  document_type: ApDocumentType;
  /** The vendor's identifier as printed: invoice number or receipt transaction number. */
  document_number: string | null;
  /** Invoice date or purchase date. */
  document_date: string | null;
  vendor_id: string | null;
  vendor_name: string;
  total_amount_cents: number;
  subtotal_amount_cents?: number | null;
  tax_amount_cents?: number | null;
  currency: string;
  po_number: string | null;
  status: ApInvoiceStatus;
  st_job_external_id: number | null;
  job_match_confidence: number | null;
  job_match_strategy: ApJobMatchStrategy | null;
  /**
   * The ServiceTitan purchase order the document's cost sits on: found from
   * its PO field, recorded by the agent, or chosen by hand. Optional until
   * the API that sends it is deployed.
   */
  st_po_external_id?: number | null;
  /**
   * "manual" when a person chose the PO — or, with a null
   * `st_po_external_id`, said no existing PO covers the document. The agent
   * keeps to either.
   */
  po_match_strategy?: "manual" | null;
  po_matched_at?: string | null;
  system_messages: ApSystemMessage[];
  /**
   * When the document was added to Volca (once its file was read): the
   * canonical list/queue sort key, shown as "Added". Not the vendor's printed
   * invoice/purchase date, and not a ServiceTitan receipt's receivedOn (when
   * the goods arrived).
   */
  created_at: string;
  /**
   * Whether a person still has to look at the agent's proposals, derived by
   * the API from the newest agent run of any status: a pending or running run,
   * a failed one, or actions that failed to apply or were rolled back all read
   * "awaiting_agent" (no badge, no filter). Optional until the API that sends
   * it is deployed.
   */
  review_state?: ApReviewState;
  /** When the document became reviewed; null unless `review_state` is "reviewed". */
  reviewed_at?: string | null;
  agent_run_id: string | null;
  /** The source attachment this document was extracted from (one attachment can yield several). */
  attachment?: ApAttachment | null;
  /** ActiveStorage URL/path for the document file (sliced PDF or original). */
  file_url?: string | null;
  file_content_type?: string | null;
}

export interface ApInvoice extends ApPurchaseDocument {
  document_type: "invoice";
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  service_address: string | null;
  invoice_items: ApInvoiceItem[];
}

/** A receipt line has the same shape as an invoice line. */
export type ApReceiptItem = ApInvoiceItem;

/** A point-of-sale receipt: a purchase already paid at the counter. */
export interface ApReceipt extends ApPurchaseDocument {
  document_type: "receipt";
  /** Store / register / transaction line as printed, e.g. "0105 00054 76585". */
  receipt_number: string | null;
  receipt_date: string | null;
  /** Register time, when printed. */
  purchased_at: string | null;
  store_number: string | null;
  store_address: string | null;
  /** Card brand, CASH, etc. */
  payment_method: string | null;
  /** Only ever the last four digits. */
  card_last4: string | null;
  auth_code: string | null;
  receipt_items: ApReceiptItem[];
}

/**
 * One ServiceTitan job returned by the AP job search, which backs the manual
 * job-match combobox.
 *
 * `id` is the internal ServiceTitan job id — it goes in the match payload and
 * is never the identifier shown to a user; `job_number` is the human-facing
 * one. Everything else is disambiguation context for two similar jobs.
 */
export interface ApJobSearchResult {
  id: number;
  job_number: string;
  customer_name: string;
  address: string;
  status: string;
  completed_on: string | null;
  business_unit: string | null;
  /** Job total in dollars; null when ServiceTitan has none. */
  total: number | null;
}

/** The existing pricebook item a `create_pricebook_item` used instead of creating one. */
export interface ApAdoptedPricebookItem {
  item_type: ApItemType;
  external_id: number;
  code: string;
  name: string | null;
}

/** Payloads keyed by action_type (see Ap::AgentAction ACTION_TYPES). */
export interface ApActionPayloads {
  [AP_ACTION_TYPE.matchJob]: { st_job_external_id: number };
  [AP_ACTION_TYPE.linkVendor]: { st_vendor_id: number; st_vendor_name: string };
  /** System-derived from a `link_vendor` proposal; see API.md. */
  [AP_ACTION_TYPE.mergeVendor]: {
    st_vendor_id: number;
    st_vendor_name: string;
    source_vendor_id: string;
    source_vendor_name: string;
    target_vendor_id: string;
    target_vendor_name: string;
  };
  [AP_ACTION_TYPE.updatePricebookCost]: {
    pricebook_item_type: ApItemType;
    pricebook_item_external_id: number;
    sku: string;
    current_cost: number;
    proposed_cost: number;
  };
  [AP_ACTION_TYPE.createPricebookItem]: {
    item_type: ApItemType;
    code: string;
    name: string;
    description: string;
    cost: number;
    /** Set on apply: the ServiceTitan id of the item created (or adopted). */
    applied_st_id?: number;
    /**
     * Set on apply when the SKU was already in the pricebook, so the existing
     * item was used instead of creating a duplicate. Its type and code can
     * differ from the proposal's, and its cost was left unchanged.
     */
    adopted_item?: ApAdoptedPricebookItem;
  };
  [AP_ACTION_TYPE.updatePo]: {
    st_purchase_order_id: number;
    /**
     * Keyed by ServiceTitan PO field (see the `api` repo's
     * Ap::AgentAction::ALLOWED_PO_CHANGE_KEYS). Most values are scalar strings,
     * but the `items` key holds a line-item array (same shape as create_po).
     */
    changes: Record<string, string | ApPoLineItem[]>;
    reason: string;
  };
  /**
   * A non-job purchase carries `non_job: true` and no `st_job_external_id` —
   * ServiceTitan files it with no job, against the tenant's business unit and
   * warehouse. Exactly one of the two is present, never both and never neither;
   * the union below makes that a type error rather than a runtime-only rule (the
   * api enforces the same invariant in Runner#validate_job_disposition).
   */
  [AP_ACTION_TYPE.createPo]:
    | {
        st_job_external_id: number;
        non_job?: never;
        st_vendor_id: number | null;
        items: ApPoLineItem[];
      }
    | {
        st_job_external_id?: never;
        non_job: true;
        st_vendor_id: number | null;
        items: ApPoLineItem[];
      };
  [AP_ACTION_TYPE.createReturn]: {
    st_job_external_id: number;
    st_vendor_id: number | null;
    st_purchase_order_id: number | null;
    reason: string;
    items: ApPoLineItem[];
  };
  /**
   * Settles an invoice that has no ServiceTitan job behind it. Its cost is
   * filed either by a `create_po` proposed alongside it, or by an existing
   * job-less purchase order named here in `st_purchase_order_id`.
   */
  [AP_ACTION_TYPE.markNonJob]: {
    reason: string;
    st_purchase_order_id?: number;
  };
  [AP_ACTION_TYPE.flagForReview]: { reason: string };
  [AP_ACTION_TYPE.noAction]: { reason: string };
}

export interface ApPoLineItem {
  sku: string;
  description: string;
  quantity: number;
  unit_cost: number;
  /**
   * Set when the line carries an item a `create_pricebook_item` in the same run
   * creates: that action's `code`. Approving the PO approves the item with it,
   * and the item is created before the PO is written.
   */
  new_pricebook_item_code?: string;
  /** The id of that `create_pricebook_item` action. */
  pricebook_item_action_id?: string;
}

/** A reviewer's free-form comment on a proposed agent action, oldest first. */
export interface ApAgentActionComment {
  id: string;
  /** The action this comment belongs to. */
  agent_action_id: string;
  content: string;
  /** Author's display name (serialized server-side); null if unknown. */
  author: string | null;
  /** Whether the current viewer may delete this comment (server-decided). */
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApAgentAction {
  id: string;
  action_type: ApActionType;
  status: ApReviewStatus;
  confidence: number;
  rationale: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  /**
   * Reviewer's display name ("Volca Admin" for admins). Is null while pending
   * review, but also for resolved actions decided by automation. So for a
   * resolved action, null-vs-present is the human/automation provenance split.
   * A blank name serializes as "", which is still a human review: always
   * compare against null strictly.
   */
  reviewed_by: string | null;
  reviewed_at: string | null;
  /** Set once an approved action is applied in ServiceTitan. */
  applied_at: string | null;
  /** Rollback lifecycle for an applied action; null if it was never applied. */
  rollback_status: ApRollbackStatus | null;
  rolled_back_at: string | null;
  /**
   * The ids of the run's `create_pricebook_item` actions whose new items this
   * PO's lines carry. Approving the PO approves the pending ones too. Empty for
   * any other action. Optional because this web can ship before the API sends
   * it.
   */
  prerequisite_action_ids?: string[];
  /**
   * Why the API would refuse to approve this action, worded for a reviewer
   * (e.g. one of its new pricebook items was dismissed). Null when approval
   * would go through, and always null once the action isn't pending. Optional
   * for the same reason as `prerequisite_action_ids`.
   */
  approval_blocked_reason?: string | null;
  /** Reviewer comments on this action, oldest first (embedded by the run serializer). */
  comments?: ApAgentActionComment[];
  /**
   * The document lines a pricebook proposal is about, paired by SKU on the
   * server; empty for every other action. Optional for older payloads.
   */
  line_item_ids?: string[];
}

export interface ApAgentRun {
  id: string;
  document_type: ApDocumentType;
  document_id: string;
  /** Set for an invoice's run, null for a receipt's. */
  invoice_id: string | null;
  /** Set for a receipt's run, null for an invoice's. */
  receipt_id: string | null;
  status: ApAgentRunStatus;
  created_at: string;
  model: string;
  turns: number;
  input_tokens: number;
  output_tokens: number;
  duration_ms: number;
  web_fetches: number;
  /** Null when the run wasn't assessed for full automation. */
  automation_qualification: ApAutomationQualification | null;
  actions: ApAgentAction[];
}

/**
 * Offset-paginated list envelope returned by the AP list endpoints when
 * `page`/`page_size` params are sent (`render_offset_paginated` server-side).
 * Deliberately no total count — pages drive load-more, not numbered pages.
 */
export interface ApPage<T> {
  items: T[];
  has_more: boolean;
  next_page: number | null;
}

/**
 * The invoice as the review queue serializes it: every scalar (including
 * `file_url`/`file_content_type` for the document preview), but none of the
 * detail-call payload — `attachment`, `invoice_items`, and `system_messages`
 * come from `invoices#show` instead. A full `ApInvoice` remains assignable.
 */
export type ApReviewQueueInvoice = Omit<
  ApInvoice,
  "attachment" | "invoice_items" | "system_messages"
>;

/**
 * A review_queue item: a (slim) invoice plus the live run whose
 * pending_review actions put it in the queue. The nested run's actions arrive
 * without `comments` — those come with the per-invoice `agent_runs` fetch.
 *
 * The inherited `agent_run_id` is the newest run of *any* status and can
 * differ from `last_completed_agent_run.id` while a newer run is in flight —
 * review UI must always use the nested run, never join via `agent_run_id`.
 */
export interface ApReviewQueueItem extends ApReviewQueueInvoice {
  last_completed_agent_run: ApAgentRun;
}

/**
 * An automated-list item: a (slim) invoice plus its newest completed run that
 * was handled by full automation. Same slim payload rules as the review queue,
 * including actions without `comments`.
 */
export interface ApAutomatedRunItem extends ApReviewQueueInvoice {
  automated_agent_run: ApAgentRun;
}

/** Browser history context for returning to a list, separate from API data. */
export interface ApInvoiceListReturnContext {
  businessId: string;
  listPath: string;
}

export interface ApReviewQueueReturnPosition
  extends ApInvoiceListReturnContext {
  invoiceIds: string[];
  invoiceIndex: number;
  invoiceCreatedAt: string;
  offset: number;
  scrollTop: number;
  pageCount: number;
}

/** Which date a document list's day filter applies to. */
export type ApDayField = "added" | "reviewed";

/**
 * Inclusive days (YYYY-MM-DD) in the business's time zone, on the date the
 * day filter is set to (added or reviewed).
 */
export interface ApDayRange {
  from: string;
  to: string;
}

export interface ApInvoicesTabFilters {
  filter: string;
  search: string;
  dayField: ApDayField;
  dayRange: ApDayRange | null;
}

/** Invoices tab state retained across navigation, scoped to the business. */
export interface ApInvoiceListState extends ApInvoicesTabFilters {
  scrollTop: number;
  scrollLeft: number;
  pageCount: number;
}

export interface ApAttachment {
  id: string;
  file_name: string;
  content_type: string;
  file_size: number;
  status: ApAttachmentStatus;
  classification_confidence: number | null;
  /** What the attachment was classified as, once it is a purchase document. */
  document_type?: ApDocumentType | null;
  /** Every invoice extracted from this attachment (a combined PDF can yield several). */
  invoice_ids: string[];
  /** Every receipt extracted from this attachment. */
  receipt_ids?: string[];
}

/**
 * Response from the single-file `invoices` create (201; a bad file is a 400
 * whose error message carries the specific reason).
 */
export interface ApUploadInvoiceResponse {
  message: string;
  attachment_id: string;
}

/**
 * Response from `invoices/bulk_upload`. Per-file failures land in `skipped`
 * rather than failing the batch; the endpoint returns 201 if anything queued
 * (even if others skipped) and 422 if nothing did. On the 422 the api client
 * rethrows an ApiError carrying only this body's `message`, not the full shape.
 */
export interface ApBulkUploadResponse {
  message: string;
  queued: { file_name: string; attachment_id: string }[];
  skipped: { file_name: string | null; error: string }[];
}

export interface ApEmailResponse {
  id: string;
  from?: string | null;
  from_address?: string | null;
  subject: string;
  received_at: string;
  message_id: string;
  status: ApEmailStatus;
  plain_body?: string | null;
  html_body?: string | null;
  attachments: ApAttachment[];
}

/** Email with normalized sender and derived display data. */
export interface ApEmail extends ApEmailResponse {
  from: string;
  message: string | undefined;
  invoiceCount: number;
  /** Receipts extracted from the email's attachments. */
  receiptCount: number;
}

export interface ApVendor {
  id: string;
  name: string;
  active: boolean;
  st_vendor_id: number | null;
  st_vendor_name: string | null;
  email_aliases: string[];
  allowed_domains: string[];
}

export interface ApVendorUpdate {
  name?: string;
  active?: boolean;
  email_aliases?: string[];
  allowed_domains?: string[];
  st_vendor_id?: number | null;
}

/** An edit to one vendor list, resolved against current data by the query layer. */
export interface ApVendorListEdit {
  field: "email_aliases" | "allowed_domains";
  action: "add" | "remove";
  value: string;
}
